import { useEffect } from "react";

import { getMonday } from "generated/api/monday";

import {
  GetGroupItemsCampaignObjective,
  GetAvailableCampaignObjectivesProgramType,
  type GetAvailableCampaignObjectivesParams,
  type GetGroupItemsParams,
} from "generated/schemas";

import { useCampaignFromTemplateContext } from "components/modals/create-campaign-from-template-modal/context/campaign-from-template-context";

type WithTransferStudentStatusFlag<TParams> = TParams & {
  transferStudentStatusFlag: boolean;
};

const buildParamsKey = (...params: Array<string | boolean>) => params.join("-");

export const useCampaignTemplateData = (
  selectedStrategy: string | undefined,
  selectedProgramType: string | undefined,
  selectedCampaignObjective: string | undefined,
  selectedTransferStudentStatusFlag: boolean | undefined,
) => {
  const {
    setCampaignTemplates,
    setIsLoadingCampaignTemplates,
    setCampaignObjectives,
    setIsLoadingCampaignObjectives,
    lastFetchedTemplatesParams,
    setLastFetchedTemplatesParams,
    lastFetchedObjectivesParams,
    setLastFetchedObjectivesParams,
  } = useCampaignFromTemplateContext();
  const transferStudentStatusFlag = selectedTransferStudentStatusFlag ?? false;

  // Fetch campaign templates when strategy and campaign objective are selected
  useEffect(() => {
    if (!selectedStrategy || !selectedCampaignObjective) {
      setCampaignTemplates([]);
      setLastFetchedTemplatesParams("");
      return;
    }

    const paramsKey = buildParamsKey(
      selectedStrategy,
      selectedCampaignObjective,
      transferStudentStatusFlag,
    );

    if (lastFetchedTemplatesParams === paramsKey) {
      return;
    }

    const fetchCampaignTemplates = async () => {
      setIsLoadingCampaignTemplates(true);
      try {
        const monday = getMonday();
        const params: WithTransferStudentStatusFlag<GetGroupItemsParams> = {
          campaignObjective: selectedCampaignObjective as GetGroupItemsCampaignObjective,
          transferStudentStatusFlag,
        };
        const results = await monday.getGroupItems({ groupId: selectedStrategy }, params);
        setCampaignTemplates(results);
        setLastFetchedTemplatesParams(paramsKey);
      } catch (error) {
        console.error("Failed to fetch campaign templates", error);
        setCampaignTemplates([]);
        setLastFetchedTemplatesParams("");
      } finally {
        setIsLoadingCampaignTemplates(false);
      }
    };

    fetchCampaignTemplates();
  }, [
    selectedStrategy,
    selectedCampaignObjective,
    transferStudentStatusFlag,
    lastFetchedTemplatesParams,
    setCampaignTemplates,
    setIsLoadingCampaignTemplates,
    setLastFetchedTemplatesParams,
  ]);

  // Fetch campaign objectives when program type and strategy are selected
  useEffect(() => {
    if (!selectedProgramType || !selectedStrategy) {
      setCampaignObjectives([]);
      setLastFetchedObjectivesParams("");
      return;
    }

    const paramsKey = buildParamsKey(
      selectedProgramType,
      selectedStrategy,
      transferStudentStatusFlag,
    );

    if (lastFetchedObjectivesParams === paramsKey) {
      return;
    }

    const fetchCampaignObjectives = async () => {
      setIsLoadingCampaignObjectives(true);
      try {
        const monday = getMonday();
        const params: WithTransferStudentStatusFlag<GetAvailableCampaignObjectivesParams> = {
          programType: selectedProgramType as GetAvailableCampaignObjectivesProgramType,
          groupId: selectedStrategy,
          transferStudentStatusFlag,
        };
        const results = await monday.getAvailableCampaignObjectives(params);
        setCampaignObjectives(results);
        setLastFetchedObjectivesParams(paramsKey);
      } catch (error) {
        console.error("Failed to fetch campaign objectives", error);
        setCampaignObjectives([]);
        setLastFetchedObjectivesParams("");
      } finally {
        setIsLoadingCampaignObjectives(false);
      }
    };

    fetchCampaignObjectives();
  }, [
    selectedProgramType,
    selectedStrategy,
    transferStudentStatusFlag,
    lastFetchedObjectivesParams,
    setCampaignObjectives,
    setIsLoadingCampaignObjectives,
    setLastFetchedObjectivesParams,
  ]);
};
